import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, PenLine, Upload, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';
import { Message } from '../time/ui';
import { DocStatus, Expiry } from './docUi';
import { viewUrl, kindLabel, fmtSize, ACCEPT } from './docUtils';

// Everyone → My documents: letters from HR (signing those that ask for it)
// and personal documents the employee uploads.

const SignPanel = ({ doc, name, onSigned, onCancel, say }) => {
    const [typed, setTyped] = useState('');
    const [agree, setAgree] = useState(false);
    const [opened, setOpened] = useState(false);
    const [busy, setBusy] = useState(false);
    const sign = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/lifecycle/documents/${doc.id}/sign`, { typedName: typed, agree });
            say(true, `Signed "${doc.title}". HR has been told.`);
            onSigned();
        } catch (err) {
            say(false, errorText(err, 'Could not sign the document'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={sign} aria-label={`Sign ${doc.title}`} style={{ display: 'grid', gap: 10, padding: 14, border: rowBorder, borderRadius: 8, marginTop: 8 }}>
            <p style={{ margin: 0 }}>
                <a href={viewUrl(doc.id)} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(true)}>Read “{doc.title}”</a> before signing{opened ? '.' : ' (opens in a new tab).'}
            </p>
            <label style={{ display: 'grid', gap: 4, maxWidth: 360 }}><span style={muted}>Type your full name: {name}</span>
                <input aria-label="Type your full name" style={field} value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off" />
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
                <span>I have read this document and I accept it. Typing my name here is my electronic signature.</span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={primary} disabled={!agree || !typed.trim() || busy}><PenLine size={14} /> {busy ? 'Signing…' : 'Sign'}</button>
                <button type="button" style={ghost} onClick={onCancel}>Cancel</button>
            </div>
        </form>
    );
};

const MyUpload = ({ meta, onDone, say }) => {
    const empty = { kind: meta.uploadKinds[0], title: '', expiresAt: '', file: null };
    const [f, setF] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [inputKey, setInputKey] = useState(0);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
    const submit = async (e) => {
        e.preventDefault();
        if (f.file && f.file.size > 10 * 1024 * 1024) { say(false, 'The file must be under 10 MB'); return; }
        const data = new FormData();
        data.append('kind', f.kind);
        data.append('title', f.title);
        if (f.expiresAt) data.append('expiresAt', f.expiresAt);
        data.append('file', f.file);
        setBusy(true);
        try {
            await api.post('/lifecycle/documents/mine', data, { headers: { 'Content-Type': 'multipart/form-data' } });
            say(true, `Uploaded "${f.title}"`);
            setF(empty);
            setInputKey(k => k + 1);
            onDone();
        } catch (err) {
            say(false, errorText(err, 'Could not upload the document'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Kind</span>
                    <select aria-label="Document kind" style={field} value={f.kind} onChange={e => set('kind', e.target.value)}>
                        {meta.uploadKinds.map(k => <option key={k} value={k}>{kindLabel(meta, k)}</option>)}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4, flex: '1 1 200px' }}><span style={muted}>Title</span>
                    <input aria-label="Document title" required maxLength={150} style={field} value={f.title} onChange={e => set('title', e.target.value)} placeholder="e.g. B.Tech degree certificate" />
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Expires on (optional)</span>
                    <input type="date" aria-label="Expires on" style={field} value={f.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
                </label>
            </div>
            <input key={inputKey} type="file" aria-label="File" accept={ACCEPT} required onChange={e => set('file', e.target.files[0] || null)} />
            <p style={{ ...muted, margin: 0 }}>PDF, PNG, JPEG or WebP, up to 10 MB. HR can see what you upload. For Aadhaar, upload a masked copy (only the last 4 digits visible).</p>
            <div><button className="btn btn-primary" style={primary} disabled={busy}><Upload size={14} /> {busy ? 'Uploading…' : 'Upload'}</button></div>
        </form>
    );
};

const MyDocuments = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const [data, setData] = useState(null);
    const [message, setMessage] = useState(null);
    const [signing, setSigning] = useState(null);
    const [uploading, setUploading] = useState(false);
    const say = useCallback((ok, text) => setMessage({ ok, text }), []);
    const load = useCallback(() => api.get('/lifecycle/documents/mine').then(r => setData(r.data)).catch(e => say(false, errorText(e, 'Could not load your documents'))), [say]);
    useEffect(() => { load(); }, [load]);

    const remove = async (doc) => {
        if (!window.confirm(`Delete "${doc.title}"?`)) return;
        try {
            await api.delete(`/lifecycle/documents/mine/${doc.id}`);
            say(true, `Deleted "${doc.title}"`);
            load();
        } catch (e) {
            say(false, errorText(e, 'Could not delete the document'));
        }
    };

    const waiting = data?.documents.filter(d => d.status === 'AWAITING_SIGNATURE') || [];
    const rest = data?.documents.filter(d => d.status !== 'AWAITING_SIGNATURE') || [];

    const row = (d) => (
        <li key={d.id} style={{ borderTop: rowBorder, padding: '12px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                    <div style={{ color: 'var(--text-main)', fontWeight: 500 }}>{d.title}</div>
                    <div style={muted}>{kindLabel(data, d.kind)}{d.size ? ` · ${fmtSize(d.size)}` : ''} · {new Date(d.createdAt).toLocaleDateString()}{d.signedAt ? ` · signed ${new Date(d.signedAt).toLocaleDateString()}` : ''}</div>
                    <Expiry date={d.expiresAt} />
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <DocStatus doc={d} />
                    <a style={ghost} href={viewUrl(d.id)} target="_blank" rel="noopener noreferrer" aria-label={`Open ${d.title}`}><ExternalLink size={14} /> Open</a>
                    {d.status === 'AWAITING_SIGNATURE' && signing !== d.id && <button className="btn btn-primary" style={primary} onClick={() => setSigning(d.id)} aria-label={`Sign ${d.title}`}><PenLine size={14} /> Sign</button>}
                    {d.uploadedBySelf && <button style={ghost} onClick={() => remove(d)} aria-label={`Delete ${d.title}`}><Trash2 size={14} /></button>}
                </div>
            </div>
            {signing === d.id && <SignPanel doc={d} name={user?.name} say={say} onCancel={() => setSigning(null)} onSigned={() => { setSigning(null); load(); }} />}
        </li>
    );

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 900 }}>
            <PageHeader crumbs={crumbsFor('documents')} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ marginBottom: 4 }}>My documents</h1>
                    <p style={{ ...muted, fontSize: 15, margin: 0 }}>Letters from HR and the documents you have shared with them.</p>
                </div>
                <button style={ghost} aria-expanded={uploading} onClick={() => setUploading(!uploading)}><Upload size={14} /> Upload a document</button>
            </div>
            <Message message={message} />
            {uploading && data && <section className="card" style={card}><MyUpload meta={data} say={say} onDone={() => { setUploading(false); load(); }} /></section>}
            {!data ? <p style={muted}>Loading…</p> : (
                <>
                    {waiting.length > 0 && (
                        <section className="card" style={{ ...card, borderLeft: '4px solid #d97706' }}>
                            <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Waiting for your signature</h2>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{waiting.map(row)}</ul>
                        </section>
                    )}
                    <section className="card" style={card}>
                        <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>All documents</h2>
                        {rest.length === 0 ? <p style={muted}>Nothing here yet.</p> : <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{rest.map(row)}</ul>}
                    </section>
                </>
            )}
        </div>
    );
};

export default MyDocuments;
